/**
 * 开发环境调试 Composable
 * @description 从 userStore 抽取的开发调试工具函数
 */

import type { Ref } from 'vue'
import type { UserInfo } from '@/types/auth'

import { PERMISSIONS } from '@/constants'
import { UserRole } from '@/types/auth'

import { DEV_ROLE_PROFILES } from './devHelpers'

/**
 * 开发调试 Composable 的依赖接口
 */
export interface DevComposableDeps {
  userInfo: Ref<UserInfo | null>
  token: Ref<string | null>
  refreshToken: Ref<string | null>
  permissions: Ref<string[]>
  loginTime: Ref<number | null>
  isLoggedIn: { readonly value: boolean }
  setToken: (newToken: string | null) => void
  setUserInfo: (info: UserInfo | null) => void
  setPermissionsFromUserInfo: (info: UserInfo | null, customPermissions?: string[]) => void
  logout: () => Promise<void>
}

/**
 * 创建开发调试 actions
 */
export function useDevActions(deps: DevComposableDeps) {
  const {
    userInfo,
    token,
    refreshToken,
    permissions,
    loginTime,
    isLoggedIn,
    setToken,
    setUserInfo,
    setPermissionsFromUserInfo,
    logout,
  } = deps

  // 开发环境助手函数 - 用于测试和调试（🔧 权限修复版本）
  function devSimulateLogin() {
    if (import.meta.env.DEV) {
      const mockUser = {
        id: 'dev-user',
        username: 'developer',
        email: 'dev@mech3d.com',
        nickname: '开发者',
        role: UserRole.ADMIN,
        isVerified: true,
        points: 1000,
        avatar: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        loginCount: 1,
      }

      setToken('dev-mock-token')
      setUserInfo(mockUser)

      // 🔧 关键修复：使用智能权限设置替代硬编码权限
      setPermissionsFromUserInfo(mockUser) // ✅ 使用基于角色的完整权限

      return true
    }
    return false
  }

  function devClearLogin() {
    if (import.meta.env.DEV) {
      logout()
      return true
    }
    return false
  }

  // 🔧 新增：多角色开发调试函数
  /**
   * 开发环境模拟不同角色登录
   * @param role 要模拟的用户角色
   * @param customOptions 自定义用户选项
   */
  function devSimulateRoleLogin(role: UserRole = UserRole.USER, customOptions: Partial<UserInfo> = {}) {
    if (import.meta.env.DEV) {
      const baseProfile = DEV_ROLE_PROFILES[role]
      const mockUser = {
        ...baseProfile,
        ...customOptions,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        loginCount: 1,
      } as UserInfo

      // 游客不需要token
      if (role !== UserRole.GUEST) {
        setToken(`dev-${role.toLowerCase()}-token`)
      }
      else {
        setToken(null)
      }

      setUserInfo(mockUser)
      setPermissionsFromUserInfo(mockUser)

      return true
    }
    return false
  }

  /**
   * 开发环境权限状态查看器
   */
  function devGetPermissionSummary() {
    if (import.meta.env.DEV) {
      const summary = {
        user: {
          id: userInfo.value?.id,
          role: userInfo.value?.role,
          nickname: userInfo.value?.nickname,
          isLoggedIn: isLoggedIn.value,
          isVerified: userInfo.value?.isVerified,
        },
        permissions: {
          total: permissions.value.length,
          list: permissions.value,
          critical: {
            taskAccept: permissions.value.includes(PERMISSIONS.USER.TASKS.ACCEPT),
            financeWithdraw: permissions.value.includes(PERMISSIONS.USER.FINANCE.WITHDRAW),
            modelManage: permissions.value.includes(PERMISSIONS.MODEL.MANAGE),
            adminWildcard: permissions.value.includes(PERMISSIONS.WILDCARD),
          },
        },
        store: {
          hasToken: !!token.value,
          tokenPreview: token.value ? `${token.value.substring(0, 20)}...` : null,
          hasRefreshToken: !!refreshToken.value,
          loginTime: loginTime.value ? new Date(loginTime.value).toISOString() : null,
        },
      }

      return summary
    }
    return null
  }

  /**
   * 开发环境快速权限测试
   * @param testPaths 要测试的路径列表
   */
  function devTestPermissions(testPaths: string[] = [
    '/user/profile',
    '/user/designer',
    '/user/provider',
    '/user/accepted-tasks',
    '/user/earnings-analysis',
    '/user/payment-withdraw',
  ]) {
    if (import.meta.env.DEV) {
      const results = testPaths.map((path) => {
        const hasBasicAccess = true // 基础访问（17页面架构都可见）

        // 检查关键权限
        let permissionStatus = 'granted'
        let requiredPermission = ''

        if (path.includes('accepted-tasks')) {
          requiredPermission = PERMISSIONS.USER.TASKS.ACCEPT
          permissionStatus = permissions.value.includes(requiredPermission) ? 'granted' : 'restricted'
        }
        else if (path.includes('earnings-analysis') || path.includes('payment-withdraw')) {
          requiredPermission = PERMISSIONS.USER.FINANCE.WITHDRAW
          permissionStatus = permissions.value.includes(requiredPermission) ? 'granted' : 'restricted'
        }

        return {
          path,
          hasBasicAccess,
          permissionStatus,
          requiredPermission,
          canUse: permissionStatus === 'granted',
        }
      })

      // const summary = {
      //   total: results.length,
      //   granted: results.filter(r => r.canUse).length,
      //   restricted: results.filter(r => !r.canUse).length,
      //   percentage: Math.round((results.filter(r => r.canUse).length / results.length) * 100),
      // }

      return results
    }
    return []
  }

  return {
    devSimulateLogin,
    devClearLogin,
    devSimulateRoleLogin,
    devGetPermissionSummary,
    devTestPermissions,
  }
}
